/**
 * The small label under a wager chip.
 *
 * Take: "<N> open" (who is waiting, never the viewer's own: the server leaves those out), else "ticket" when
 * one is held at that wager. Create: "<N> yours" (the viewer's own open challenges at that stake, nothing
 * when none), "ticket" for a held ticket alone, and both as "<N> yours" followed by the small ticket glyph
 * on one line — the word did not fit a chip at 320-390 px (Blockari's `chipHint`, the same rule). The slot is
 * the chip's own bottom edge: none of these changes its height.
 */

import { fmtAmount, ownCustomCount } from '@/ui/fee';

/** Take's chip label. */
export function takeChipHint(open: number, ticketHeld: boolean): string {
  return open > 0 ? `${open} open` : ticketHeld ? 'ticket' : '';
}

/** Create's chip label: the text, and whether the ticket glyph follows it (a count with a held ticket). */
export function createChipHint(yours: number, ticketHeld: boolean): { text: string; glyph: boolean } {
  if (yours > 0) return { text: `${yours} yours`, glyph: ticketHeld };
  return { text: ticketHeld ? 'ticket' : '', glyph: false };
}

/**
 * How many of the viewer's own open challenges the CUSTOM tile speaks for: idle, every one at an amount that is not a
 * quick pick; once an amount is committed (`committedFee`, the amount the tile now shows), only the ones at THAT amount.
 */
export function customYours(
  byFee: Readonly<Record<string, number | undefined>>,
  quick: readonly number[],
  committedFee: number | null,
): number {
  return committedFee === null ? ownCustomCount(byFee, quick) : (byFee[String(committedFee)] ?? 0);
}

/**
 * Create's CUSTOM tile label: the same "<N> yours" as the quick tiles (see `customYours`). A ticket cannot cover a
 * custom amount, so no glyph. With none, the tile says what it is: 'any amount' while idle, 'custom' once an amount is
 * committed. So "75 / 2 yours" is never a claim about other amounts.
 */
export function customChipHint(
  byFee: Readonly<Record<string, number | undefined>>,
  quick: readonly number[],
  committedFee: number | null,
): string {
  const yours = customYours(byFee, quick, committedFee);
  if (yours > 0) return createChipHint(yours, false).text;
  return committedFee === null ? 'any amount' : 'custom';
}

/**
 * A Create chip is ONE tap target: the amount, and under it the "<N> yours" caption as plain text (no link, no
 * second button, nothing to tap but the chip). Its accessible name carries what the caption says: "10, 1 of yours
 * open" (", ticket held" when a ticket is held at that wager, which the caption shows as a glyph). Null when there is
 * nothing to add, so the chip keeps its own text as its name. `label` is the chip's face ("10", "Custom", "75").
 */
export function chipAriaLabel(label: string, yours: number, ticketHeld = false): string | null {
  if (yours <= 0) return null;
  return `${label}, ${yours} of yours open${ticketHeld ? ', ticket held' : ''}`;
}

/** The quick chip's name: its amount, formatted as the chip prints it. */
export const quickChipAriaLabel = (fee: number, yours: number, ticketHeld = false): string | null =>
  chipAriaLabel(fmtAmount(fee), yours, ticketHeld);
